import React, { useState, useEffect } from 'react';
import { TechlogEntry, Sector, FlightType } from '../types';
import { addTechlogEntry, updateTechlogEntry, updateAircraftStats, loadFromLocalStorage } from '../utils/storage';
import { calculateAircraftStats } from '../utils/calculations';
import { getPilots, getAircraft } from '../utils/supabaseQueries';
import type { Pilot, Aircraft } from '../types/database';

interface TechlogEntryFormProps {
  onEntryAdded: () => void;
  editingEntry?: TechlogEntry | null;
}

// Airport/Location list
const AIRPORTS = [
  'Addis Ababa', 'Amboseli', 'Angama', 'Antanarivo', 'Arusha',
  'Bagamoyo', 'Bangui', 'Barani', 'Basuto', 'Behobeho', 'Beira (Mozam)', 'Beno Beno',
  'Biharamulo', 'Blantyre', 'Buckreef', 'Bujumbura', 'Bukavu', 'Bukoba', 'Bulyankhulu',
  'Bunia', 'Burigi', 'Burob', 'Chankode', 'Chunya',
  'Dar', 'Diredawa', 'Djibouti', 'Dodoma', 'Doko',
  'Eldoret', 'El Fashir', 'Engassumet', 'Entebbe',
  'Fish Eagle', 'Fort Ikoma',
  'Geita', 'Gelai', 'Geneina', 'Golden Pride', 'Goma', 'Gomba', 'Grumeti', 'Harare',
  'Ifakara', 'Iku', 'Ilovo', 'Ipera', 'Iringa', 'Isanga', 'Itigi',
  'Jinja', 'Johannesburg', 'Jomo Kenyatta', 'Jongomero', 'Juba',
  'Kabojo', 'Kagera', 'Kahama', 'Kakira', 'Kalulu', 'Kapalagulu', 'Karagwe', 'Karonga',
  'Kasense', 'Kasoli', 'Kasulu', 'Katavi', 'Keekorok', 'Khartoum', 'Kiba', 'Kibaoni',
  'Kibaya', 'Kiboko', 'Kiboko Selous', 'Kibondo', 'Kichwa Tembo', 'Kigali', 'Kigoma',
  'Kigozi', 'Kigozi2', 'Kihansi', 'Kilimanjaro', 'Kilombero', 'Kilomera', 'Kilosa',
  'Kilumbi', 'Kilwa', 'Kilwa Masoko', 'Kimali', 'Kingupira', 'Kinshasa', 'Kinyanguru',
  'Kipili', 'Kirawira', 'Kirawira2', 'Kirondatal', 'Kirumira', 'Kisangani', 'Kisoro',
  'Kiswa', 'Kizigo', 'Kleins Camp', 'Koga', 'Kogatende', 'Kondoa', 'Kongwa', 'Kuro',
  'Kusini', 'Kwajoni',
  'Laivera', 'Lamai', 'Lamu', 'Lanseria', 'Lengai', 'Ligombe', 'Likuyu', 'Lilongwe',
  'Lindi', 'Liuli', 'Livingstone', 'Liwale', 'Lobo', 'Loiborserit', 'Lokichogio',
  'Loliondo', 'Luanshya', 'Lugala', 'Lugonesi', 'Luhanyando', 'Lukula', 'Lukwati',
  'Lunda', 'Lusaka', 'Luwego', 'Lwafi',
  'Maboxi', 'Madaba', 'Madaba NA', 'Mafia', 'Mafinga', 'Mahale', 'Makau', 'Makere',
  'Makwaja', 'Makwasa', 'Malindi', 'Mamarehe', 'Mamire', 'Manyoni', 'Mara North',
  'Mara Serena', 'Masasi', 'Mashado', 'Matambwe', 'Matemanga', 'Matemaupinde',
  'Mbarahi', 'Mbesa', 'Mbeya', 'Mbiga', 'Mbono', 'Mbuga', 'Mbuyu', 'Merugoi',
  'Mgololo', 'Mhuba', 'Migrurwe', 'Mikumi', 'Mikuyu', 'Milembwe', 'Mishamo',
  'Misungwi', 'Mkangira', 'Mkomazi', 'Mkondo', 'Mkongonero', 'Mkuyu', 'Mlahi',
  'Mlay', 'Mlele', 'Mleleram', 'Mngeta', 'Mombo', 'Moroni', 'Moshi', 'Mpululu',
  'Mpunde', 'Msenguni', 'Msolwa', 'Mtemere', 'Mtende', 'Mtera', 'Mtetesi', 'Mtibwa',
  'Mufindi', 'Mugumu', 'Muhesi', 'Murungu', 'Murugwanza', 'Musiara', 'Mwadui', 'Mwiba', 'Mzuzu',
  'Naberera', 'Naibormut', 'Nampula', 'Nanyuki', 'Ndjamena', 'Ndola', 'Ndutu',
  'New Luika', 'Newala', 'Ngerengere', 'Ngorongoro', 'Ngualla', 'Ngwazi', 'Niensi',
  'Niensi (Old)', 'Njombe', 'Nkoname', 'Nyala', 'Nyonga', 'Nzega',
  'Olduvai', 'Olare Orok', 'Olare Oruk', 'Olkiombo', 'Olseki',
  'Pangani', 'Pasan', 'Pemba', 'Pemba (Mozam)',
  'Ras Kutani', 'Ruaha', 'Rubia', 'Rufui', 'Rungwa', 'Rushungi', 'Ruvu',
  'Saadani', 'Sadaani', 'Samase', 'Same', 'Sanzare', 'Sasakwa', 'Sasi', 'Segera',
  'Seronera', 'Serengeti N.', 'Serengeti South', 'Siana', 'Sidai', 'Simanjiro',
  'Simba', 'Singida', 'Sire', 'Sitalike', 'Siwandu', 'Songo Songo', 'Steiglers',
  'Stiegler\'s', 'Sumbazi',
  'Talamai', 'Tarangire', 'Tarime', 'Tete', 'Tikas', 'Tingi', 'Tula', 'Tunduru',
  'Tura', 'Turiani',
  'Ugalla', 'Urambo', 'Ushirombo', 'Uvinza',
  'Victoria Falls',
  'Waso', 'West Kilimanjaro', 'Wilson',
  'Yeye', 'Yeye2',
  'Zanzibar',
];

// Autocomplete Input Component
interface AutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder?: string;
  required?: boolean;
}

const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
  value,
  onChange,
  options,
  placeholder,
  required,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<string[]>(options);

  const handleInputChange = (inputValue: string) => {
    onChange(inputValue);
    
    if (inputValue.trim() === '') {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter((option) =>
        option.toLowerCase().includes(inputValue.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
    setShowDropdown(true);
  };

  const handleSelectOption = (option: string) => {
    onChange(option);
    setShowDropdown(false);
  };

  const handleFocus = () => {
    setFilteredOptions(options);
    setShowDropdown(true);
  };

  const handleBlur = () => {
    // Delay to allow click on dropdown item
    setTimeout(() => setShowDropdown(false), 200);
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        placeholder={placeholder}
        required={required}
      />
      {showDropdown && filteredOptions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
          {filteredOptions.map((option, index) => (
            <div
              key={index}
              onMouseDown={() => handleSelectOption(option)}
              className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TechlogEntryForm: React.FC<TechlogEntryFormProps> = ({ onEntryAdded, editingEntry }) => {
  const isEditMode = !!editingEntry;
  const [formData, setFormData] = useState({
    techlogNumber: '',
    date: new Date().toISOString().split('T')[0],
    aircraftRegistration: '',
    pilotName: '',
    coPilotName: '',
    flightType: 'S-Schedule' as FlightType,
    // Trend data fields
    oat: undefined as number | undefined,
    alt: undefined as number | undefined,
    ias: undefined as number | undefined,
    tas: undefined as number | undefined,
    torque: undefined as number | undefined,
    propRpm: undefined as number | undefined,
    itt: undefined as number | undefined,
    ng: undefined as number | undefined,
    oilPress: undefined as number | undefined,
    oilTemp: undefined as number | undefined,
    fuelFlow: undefined as number | undefined,
  });

  const [pilots, setPilots] = useState<Pilot[]>([]);
  const [aircraft, setAircraft] = useState<Aircraft[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  // Load pilots and aircraft on mount
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const [pilotsData, aircraftData] = await Promise.all([
          getPilots(),
          getAircraft(),
        ]);
        setPilots(pilotsData);
        setAircraft(aircraftData);
      } catch (error) {
        console.error('Error loading pilots/aircraft:', error);
      } finally {
        setLoadingData(false);
      }
    };

    loadDropdownData();
  }, []);

  // Populate form when editing an entry
  useEffect(() => {
    if (editingEntry) {
      setFormData({
        techlogNumber: editingEntry.techlogNumber,
        date: editingEntry.date,
        aircraftRegistration: editingEntry.aircraftRegistration,
        pilotName: editingEntry.pilotName,
        coPilotName: editingEntry.coPilotName || '',
        flightType: editingEntry.flightType || 'S-Schedule',
        oat: editingEntry.oat,
        alt: editingEntry.alt,
        ias: editingEntry.ias,
        tas: editingEntry.tas,
        torque: editingEntry.torque,
        propRpm: editingEntry.propRpm,
        itt: editingEntry.itt,
        ng: editingEntry.ng,
        oilPress: editingEntry.oilPress,
        oilTemp: editingEntry.oilTemp,
        fuelFlow: editingEntry.fuelFlow,
      });
      setSectors(editingEntry.sectors || []);
    }
  }, [editingEntry]);

  const [sectors, setSectors] = useState<Sector[]>([
    {
      id: '1',
      departure: '',
      arrival: '',
      takeoffTime: '',
      landingTime: '',
      flightTime: 0,
      itt: '' as any,
      fuelUplift: undefined,
      oilUplift: undefined,
    },
  ]);

  const handleInputChange = (field: string, value: string) => {
    setSubmitSuccess(null); // Clear success message when user starts editing
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value,
      };
      
      // If pilot is selected and they're the same as co-pilot, clear co-pilot
      if (field === 'pilotName' && value === prev.coPilotName) {
        updated.coPilotName = '';
      }
      
      return updated;
    });
  };

  const handleSectorChange = (index: number, field: string, value: string | number) => {
    setSubmitSuccess(null); // Clear success message when user edits sectors
    setSectors(prev => prev.map((sector, i) => 
      i === index ? { ...sector, [field]: value } : sector
    ));
  };

  const handleAddSector = () => {
    const lastSector = sectors.length > 0 ? sectors[sectors.length - 1] : null;
    const newSector: Sector = {
      id: Date.now().toString(),
      departure: lastSector?.arrival || '',
      arrival: '',
      takeoffTime: '',
      landingTime: '',
      flightTime: 0,
      itt: '' as any,
      fuelUplift: undefined,
      oilUplift: undefined,
    };
    setSectors(prev => [...prev, newSector]);
  };

  const handleRemoveSector = (index: number) => {
    if (sectors.length > 1) {
      setSectors(prev => prev.filter((_, i) => i !== index));
    }
  };

  const calculateFlightTime = (takeoffTime: string, landingTime: string): number => {
    if (!takeoffTime || !landingTime) return 0;
    
    const takeoff = new Date(`2000-01-01T${takeoffTime}`);
    const landing = new Date(`2000-01-01T${landingTime}`);
    
    if (landing < takeoff) {
      landing.setDate(landing.getDate() + 1);
    }
    
    const diffMs = landing.getTime() - takeoff.getTime();
    return diffMs / (1000 * 60 * 60);
  };

  const handleTimeChange = (index: number, field: 'takeoffTime' | 'landingTime', value: string) => {
    handleSectorChange(index, field, value);
    
    const sector = sectors[index];
    const otherField = field === 'takeoffTime' ? 'landingTime' : 'takeoffTime';
    const otherTime = sector[otherField];
    
    if (value && otherTime) {
      const flightTime = calculateFlightTime(
        field === 'takeoffTime' ? value : otherTime,
        field === 'landingTime' ? value : otherTime
      );
      handleSectorChange(index, 'flightTime', Math.floor(flightTime * 10) / 10);
    }
  };

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.techlogNumber || !formData.aircraftRegistration || !formData.pilotName) {
      alert('Please fill in all required fields');
      return;
    }

    const validSectors = sectors.filter(sector => 
      sector.departure && sector.arrival && sector.takeoffTime && sector.landingTime
    );

    if (validSectors.length === 0) {
      alert('Please add at least one valid sector');
      return;
    }

    const entry: TechlogEntry = {
      id: isEditMode ? editingEntry!.id : Date.now().toString(),
      techlogNumber: formData.techlogNumber,
      date: formData.date,
      aircraftRegistration: formData.aircraftRegistration.toUpperCase(),
      pilotName: formData.pilotName,
      coPilotName: formData.coPilotName || undefined,
      flightType: formData.flightType,
      sectors: validSectors,
      createdAt: isEditMode ? editingEntry!.createdAt : new Date(),
      // Trend data
      oat: formData.oat,
      alt: formData.alt,
      ias: formData.ias,
      tas: formData.tas,
      torque: formData.torque,
      propRpm: formData.propRpm,
      itt: formData.itt,
      ng: formData.ng,
      oilPress: formData.oilPress,
      oilTemp: formData.oilTemp,
      fuelFlow: formData.fuelFlow,
    };

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      if (isEditMode) {
        await updateTechlogEntry(entry);
        setSubmitSuccess(`Techlog ${formData.techlogNumber} updated successfully!`);
      } else {
        await addTechlogEntry(entry);
        setSubmitSuccess(`Techlog ${formData.techlogNumber} submitted successfully!`);
      }
      
      // Update aircraft stats
      const data = loadFromLocalStorage();
      const aircraftStats = calculateAircraftStats(formData.aircraftRegistration.toUpperCase(), data.techlogEntries);
      updateAircraftStats(aircraftStats);

      // Reset form
      setFormData({
        techlogNumber: '',
        date: new Date().toISOString().split('T')[0],
        aircraftRegistration: '',
        pilotName: '',
        coPilotName: '',
        flightType: 'S-Schedule' as FlightType,
        oat: undefined,
        alt: undefined,
        ias: undefined,
        tas: undefined,
        torque: undefined,
        propRpm: undefined,
        itt: undefined,
        ng: undefined,
        oilPress: undefined,
        oilTemp: undefined,
        fuelFlow: undefined,
      });
      setSectors([{
        id: '1',
        departure: '',
        arrival: '',
        takeoffTime: '',
        landingTime: '',
        flightTime: 0,
        itt: '' as any,
        fuelUplift: undefined,
        oilUplift: undefined,
      }]);

      onEntryAdded();

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(null);
      }, 5000);
    } catch (error) {
      console.error('Error submitting techlog entry:', error);
      setSubmitError('Failed to save techlog entry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">
        {isEditMode ? 'Edit Techlog Entry' : 'Daily Techlog Entry'}
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Success Message */}
        {submitSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <p className="text-sm text-green-800 font-medium">✓ {submitSuccess}</p>
          </div>
        )}

        {/* Error Message */}
        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-800">{submitError}</p>
          </div>
        )}

        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="techlogNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Techlog Number *
            </label>
            <input
              type="text"
              id="techlogNumber"
              value={formData.techlogNumber}
              onChange={(e) => handleInputChange('techlogNumber', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="12345"
              required
            />
          </div>
          
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
              Date *
            </label>
            <input
              type="date"
              id="date"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <div>
            <label htmlFor="aircraftRegistration" className="block text-sm font-medium text-gray-700 mb-1">
              Aircraft Registration *
            </label>
            <select
              id="aircraftRegistration"
              value={formData.aircraftRegistration}
              onChange={(e) => handleInputChange('aircraftRegistration', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={loadingData}
            >
              <option value="">Select aircraft...</option>
              {aircraft.map((a) => (
                <option key={a.id} value={a.registration}>
                  {a.registration} {a.model && `(${a.model})`}
                </option>
              ))}
            </select>
            {loadingData && (
              <p className="text-xs text-gray-500 mt-1">Loading aircraft...</p>
            )}
          </div>
          
          <div>
            <label htmlFor="pilotName" className="block text-sm font-medium text-gray-700 mb-1">
              Pilot Name *
            </label>
            <select
              id="pilotName"
              value={formData.pilotName}
              onChange={(e) => handleInputChange('pilotName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={loadingData}
            >
              <option value="">Select pilot...</option>
              {pilots.map((p) => (
                <option key={p.id} value={p.name}>
                  {p.name} {p.license_number && `(${p.license_number})`}
                </option>
              ))}
            </select>
            {loadingData && (
              <p className="text-xs text-gray-500 mt-1">Loading pilots...</p>
            )}
          </div>
          
          <div>
            <label htmlFor="coPilotName" className="block text-sm font-medium text-gray-700 mb-1">
              Co-Pilot Name (Optional)
            </label>
            <select
              id="coPilotName"
              value={formData.coPilotName}
              onChange={(e) => handleInputChange('coPilotName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loadingData}
            >
              <option value="">Select co-pilot (optional)...</option>
              {pilots
                .filter((p) => p.name !== formData.pilotName)
                .map((p) => (
                  <option key={p.id} value={p.name}>
                    {p.name} {p.license_number && `(${p.license_number})`}
                  </option>
                ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="flightType" className="block text-sm font-medium text-gray-700 mb-1">
              Flight Type *
            </label>
            <select
              id="flightType"
              value={formData.flightType}
              onChange={(e) => handleInputChange('flightType', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="S-Schedule">S-Schedule</option>
              <option value="C-Charter">C-Charter</option>
              <option value="ME-Medevac">ME-Medevac</option>
              <option value="T-Training">T-Training</option>
              <option value="MT-Maintenance">MT-Maintenance</option>
            </select>
          </div>
        </div>

        {/* Trend Data Section - At Techlog Level */}
        <div className="mt-6 pt-6 border-t-2 border-gray-300">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Trend Data (Optional)</h3>
          <p className="text-sm text-gray-600 mb-4">Record aircraft cruise performance parameters for this flight</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label htmlFor="oat" className="block text-sm font-medium text-gray-700 mb-1">
                OAT (°C)
              </label>
              <input
                type="number"
                id="oat"
                value={formData.oat || ''}
                onChange={(e) => handleInputChange('oat', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.01"
                placeholder="Optional"
              />
            </div>

            <div>
              <label htmlFor="alt" className="block text-sm font-medium text-gray-700 mb-1">
                ALT (FT)
              </label>
              <input
                type="number"
                id="alt"
                value={formData.alt || ''}
                onChange={(e) => handleInputChange('alt', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="1"
                min="0"
                placeholder="Optional"
              />
            </div>

            <div>
              <label htmlFor="ias" className="block text-sm font-medium text-gray-700 mb-1">
                IAS (knots)
              </label>
              <input
                type="number"
                id="ias"
                value={formData.ias || ''}
                onChange={(e) => handleInputChange('ias', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.1"
                min="0"
                placeholder="Optional"
              />
            </div>

            <div>
              <label htmlFor="tas" className="block text-sm font-medium text-gray-700 mb-1">
                TAS (knots)
              </label>
              <input
                type="number"
                id="tas"
                value={formData.tas || ''}
                onChange={(e) => handleInputChange('tas', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.1"
                min="0"
                placeholder="Optional"
              />
            </div>

            <div>
              <label htmlFor="torque" className="block text-sm font-medium text-gray-700 mb-1">
                Torque (FT.LBS)
              </label>
              <input
                type="number"
                id="torque"
                value={formData.torque || ''}
                onChange={(e) => handleInputChange('torque', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.01"
                min="0"
                placeholder="Optional"
              />
            </div>

            <div>
              <label htmlFor="propRpm" className="block text-sm font-medium text-gray-700 mb-1">
                Prop RPM (1/MIN)
              </label>
              <input
                type="number"
                id="propRpm"
                value={formData.propRpm || ''}
                onChange={(e) => handleInputChange('propRpm', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.1"
                min="0"
                placeholder="Optional"
              />
            </div>

            <div>
              <label htmlFor="itt" className="block text-sm font-medium text-gray-700 mb-1">
                Cruise ITT (°C)
              </label>
              <input
                type="number"
                id="itt"
                value={formData.itt || ''}
                onChange={(e) => handleInputChange('itt', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.01"
                min="0"
                placeholder="Optional"
              />
            </div>

            <div>
              <label htmlFor="ng" className="block text-sm font-medium text-gray-700 mb-1">
                NG (%)
              </label>
              <input
                type="number"
                id="ng"
                value={formData.ng || ''}
                onChange={(e) => handleInputChange('ng', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.01"
                min="0"
                max="100"
                placeholder="Optional"
              />
            </div>

            <div>
              <label htmlFor="oilPress" className="block text-sm font-medium text-gray-700 mb-1">
                Oil Press (PSI)
              </label>
              <input
                type="number"
                id="oilPress"
                value={formData.oilPress || ''}
                onChange={(e) => handleInputChange('oilPress', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.1"
                min="0"
                placeholder="Optional"
              />
            </div>

            <div>
              <label htmlFor="oilTemp" className="block text-sm font-medium text-gray-700 mb-1">
                Oil Temp (°C)
              </label>
              <input
                type="number"
                id="oilTemp"
                value={formData.oilTemp || ''}
                onChange={(e) => handleInputChange('oilTemp', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.1"
                placeholder="Optional"
              />
            </div>

            <div>
              <label htmlFor="fuelFlow" className="block text-sm font-medium text-gray-700 mb-1">
                Fuel Flow (LBS/HR)
              </label>
              <input
                type="number"
                id="fuelFlow"
                value={formData.fuelFlow || ''}
                onChange={(e) => handleInputChange('fuelFlow', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.01"
                min="0"
                placeholder="Optional"
              />
            </div>
          </div>
        </div>

        {/* Sectors */}
        <div>
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Sectors</h3>
          </div>
          
          <div className="space-y-4">
            {sectors.map((sector, index) => (
              <div key={sector.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium text-gray-700">Sector {index + 1}</h4>
                  {sectors.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveSector(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Departure *
                    </label>
                    <AutocompleteInput
                      value={sector.departure}
                      onChange={(value) => handleSectorChange(index, 'departure', value)}
                      options={AIRPORTS}
                      placeholder="Search or select..."
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Arrival *
                    </label>
                    <AutocompleteInput
                      value={sector.arrival}
                      onChange={(value) => handleSectorChange(index, 'arrival', value)}
                      options={AIRPORTS}
                      placeholder="Search or select..."
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Takeoff Time *
                    </label>
                    <input
                      type="time"
                      value={sector.takeoffTime}
                      onChange={(e) => handleTimeChange(index, 'takeoffTime', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Landing Time *
                    </label>
                    <input
                      type="time"
                      value={sector.landingTime}
                      onChange={(e) => handleTimeChange(index, 'landingTime', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Flight Time (hours)
                    </label>
                    <input
                      type="number"
                      value={sector.flightTime}
                      onChange={(e) => handleSectorChange(index, 'flightTime', parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      step="0.1"
                      min="0"
                      readOnly
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ITT *
                    </label>
                    <input
                      type="number"
                      value={sector.itt}
                      onChange={(e) => handleSectorChange(index, 'itt', e.target.value ? parseFloat(e.target.value) : '')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fuel Uplift (Ltrs)
                    </label>
                    <input
                      type="number"
                      value={sector.fuelUplift || ''}
                      onChange={(e) => handleSectorChange(index, 'fuelUplift', e.target.value ? parseFloat(e.target.value) : '')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      step="0.01"
                      min="0"
                      placeholder="Optional"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Oil Uplift (qts)
                    </label>
                    <input
                      type="number"
                      value={sector.oilUplift || ''}
                      onChange={(e) => handleSectorChange(index, 'oilUplift', e.target.value ? parseFloat(e.target.value) : '')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      step="0.01"
                      min="0"
                      placeholder="Optional"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={handleAddSector}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Add Sector
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting 
              ? (isEditMode ? 'Updating...' : 'Submitting...') 
              : (isEditMode ? 'Update Techlog Entry' : 'Submit Techlog Entry')
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default TechlogEntryForm;
